import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import './ai-chat.css'

// Global bridge interface for plain JS integration (window.askAiBridge)
declare global {
  interface Window {
    askAiBridge?: {
      open: () => void
      ask: (q: string) => void
    }
  }
}

/**
 * AskAI popup component
 * - Renders the fixed CTA and the popup markup you shared
 * - Streams responses, renders Markdown (marked + DOMPurify)
 * - Shows featured links and next-question chips (from meta call)
 * - Preserves your classNames so your existing CSS applies unchanged
 */

const isBrowser = typeof window !== 'undefined'
// GA4 clientId helper (best-effort, sync)
function getGAClientId(): string {
  try {
    // Try GA4 cookie `_ga=GAx.x.clientId` -> return `clientId` part
    const m = document.cookie.match(/(?:^|;\s*)_ga=GA\d+\.\d+\.(\d+\.\d+)/)
    if (m && m[1]) return m[1]
  } catch {}
  try {
    // Universal Analytics fallback
    const ga = (window as any).ga
    if (ga && ga.getAll) {
      const trackers = ga.getAll()
      if (trackers && trackers.length) {
        const id = trackers[0].get('clientId')
        if (id) return id
      }
    }
  } catch {}
  return ''
}
function processLinks(html: string) {
  let updated = html.replace(/<a /g, '<a target="_blank" rel="noopener noreferrer" ')
  updated = updated.replace(/>\s*\*\s*/g, '>')
  return updated
}
// ---- helpers to lazy-load marked + DOMPurify (as in your JS) ----
function loadScript(src: string) {
  return new Promise<void>((resolve, reject) => {
    const s = document.createElement('script')
    s.src = src
    s.onload = () => resolve()
    s.onerror = e => reject(e)
    document.head.appendChild(s)
  })
}

async function ensureMarkdownLibs() {
  if (!isBrowser) return
  if (!(window as any).marked) {
    await loadScript('https://cdn.jsdelivr.net/npm/marked/marked.min.js')
  }
  if (!(window as any).DOMPurify) {
    await loadScript('https://cdn.jsdelivr.net/npm/dompurify/dist/purify.min.js')
  }
  ;(window as any).marked.setOptions({
    gfm: true,
    breaks: true,
    mangle: false,
    headerIds: false,
    smartypants: true,
  })
}

function normalizeMarkdown(md: string) {
  if (!md) return md
  let out = md.replace(/[\u200B\u200C\u200D\u2060\uFEFF]/g, '')
  out = out.replace(/\[(https?:\/\/[^\]\s]+)\](?!\()/g, (_: any, url: string) => {
    const cleaned = url.replace(/\s+/g, '')
    return `[${cleaned}](${cleaned})`
  })
  out = out.replace(/\[((?:www\.)[^\]\s]+)\](?!\()/gi, (_: any, host: string) => {
    const cleaned = host.replace(/\s+/g, '')
    return `[${cleaned}](https://${cleaned})`
  })
  out = out.replace(/`(https?:\/\/[^`\s]+)`/g, '<$1>')
  out = out.replace(/(^|\s)(https?:\/\/[^\s<)]+)(?!\))/g, '$1<$2>')
  // Autolink bare www.* domains
  out = out.replace(/(^|\s)(www\.[^\s<)]+)(?!\))/gi, '$1<https://$2>')
  return out
}

function fixParagraphBulletsInHTML(html: string) {
  if (!html) return html
  // Turn paragraphs that begin with "*" or "-" into proper list items,
  // then wrap consecutive <li> blocks into a <ul>.
  let s = html.replace(/<p>\s*[*-]\s+(.+?)<\/p>/g, '<li>$1</li>')
  // Wrap any consecutive <li> groups with a <ul>
  s = s.replace(/(?:\s*<li>[\s\S]*?<\/li>\s*)+/g, (m: string) => `<ul>${m}</ul>`)
  return s
}

function renderMarkdownToHTML(md: string) {
  if (!isBrowser || !(window as any).marked) return md

  // Keep raw markdown intact for correct list parsing by `marked`.
  // Ensure CRLF normalization and add a blank line before bullet starters to help GFM lists.
  let pre = (md || '').replace(/\r/g, '')
  pre = pre.replace(/([^\n])\n(\s*[*-]\s+)/g, (_m, a, b) => `${a}\n\n${b}`)

  // Parse markdown first (so leading "* " becomes <li> and not literal '*')
  let html = (window as any).marked.parse(pre)

  // Sanitize the HTML
  let safe = (window as any).DOMPurify ? (window as any).DOMPurify.sanitize(html) : html

  // Post-HTML cleanups:
  // 1) Fix any straggler paragraphs that still show '*' as text
  safe = fixParagraphBulletsInHTML(safe)
  // 2) Ensure external links open in a new tab and are safe
  safe = processLinks(safe)

  return safe
}

/**
 * Collapse duplicate clauses that appear twice in the same paragraph/line due to streaming merges.
 * Looks for a repeated tail fragment inside a single line/sentence and trims the second copy.
 */
function collapseIntraLineRepeats(md: string): string {
  if (!md) return md
  const lines = md.split('\n')
  const out: string[] = []
  for (let raw of lines) {
    let s = raw
    // Only attempt on reasonably long lines (typical when bullets wrap as one line)
    if (s.length > 120) {
      // Try to find a tail fragment that appears earlier in the same line.
      // We scan tail sizes from 200 down to 40 for a robust match.
      const maxTail = Math.min(200, Math.floor(s.length / 2))
      let trimmed = false
      for (let L = maxTail; L >= 40; L -= 4) {
        const tail = s.slice(-L)
        const firstIdx = s.indexOf(tail)
        const lastIdx = s.lastIndexOf(tail)
        if (firstIdx !== -1 && lastIdx !== -1 && firstIdx < lastIdx) {
          // We detected the same contiguous fragment twice; keep only the first occurrence
          s = s.slice(0, lastIdx)
          trimmed = true
          break
        }
      }
      // Secondary safeguard: if the last ~140 chars are mostly a repeat, remove the final duplicate comma/word spillover
      if (!trimmed && s.length > 240) {
        const probe = s.slice(-140)
        const idx = s.indexOf(probe)
        if (idx !== -1 && idx < s.length - probe.length - 8) {
          s = s.slice(0, idx + probe.length)
        }
      }
      // Neaten broken trailing punctuation after trimming
      s = s.replace(/\s*[,;:.-]+\s*$/, match =>
        /[)]/.test(s.slice(0, -match.length)) ? '' : match.trim()
      )
    }
    out.push(s)
  }
  return out.join('\n')
}

/**
 * Merge a streaming delta into the current buffer while handling overlapping
 * re-sent prefixes. Prevents duplicate paragraphs when providers resend context.
 */
function mergeChunk(buffer: string, delta: string): string {
  if (!delta) return buffer
  if (!buffer) return delta

  // If the provider resent the whole accumulated text, prefer replacement
  if (delta.startsWith(buffer)) return delta

  // If this smaller delta is already contained, skip it
  if (buffer.includes(delta)) return buffer

  // Append only the non-overlapping tail
  const max = Math.min(buffer.length, delta.length)
  for (let k = Math.min(2000, max); k > 0; k--) {
    if (buffer.endsWith(delta.slice(0, k))) {
      return buffer + delta.slice(k)
    }
  }
  return buffer + delta
}

/**
 * Fix malformed / duplicated link patterns produced by streaming (e.g.,
 * nested "https://https://" or repeated bracket-wrapped URLs).
 */
function fixMalformedLinks(md: string): string {
  if (!md) return md
  let s = md

  // 1) Collapse multiple protocol prefixes: https://https://...
  s = s.replace(/https?:\/\/(https?:\/\/)+/g, 'https://')

  // 2) Turn [https://example.com] or [[https://example.com]] into proper markdown link
  s = s.replace(/\[\s*(https?:\/\/[^\]\s]+)\s*\](?!\()/gi, '[$1]($1)')
  s = s.replace(/\[+\s*(https?:\/\/[^\]\s]+)\s*\]+/gi, '[$1]($1)')

  // 3) If a link target itself starts with duplicated protocol inside parentheses
  s = s.replace(/\((https?:\/\/)+(https?:\/\/[^)\s]+)\)/g, '($2)')

  // 4) Remove accidental nested bracketed links like [ [https://...] ]
  s = s.replace(/\[\s*\[\s*(https?:\/\/[^\]\s]+)\s*\]\s*\]/gi, '[$1]($1)')

  // 5) Cleanup variants like:
  //    https://example.com(https://example.com)
  //    `https://example.com`(https://example.com)
  //    <https://example.com>(https://example.com)
  //    and with spaces inside parentheses
  s = s.replace(
    /`?\s*<?\s*(https?:\/\/[^\s>)]+)\s*>?\s*`?\s*\(\s*`?\s*<?\s*\1\s*>?\s*`?\s*\)/gi,
    '<$1>'
  )

  // 6) Also collapse when the duplication happens after a proper markdown link:
  //    [Title](https://x)(https://x) -> [Title](https://x)
  s = s.replace(/\[([^\]]+)\]\(\s*(https?:\/\/[^\s)]+)\s*\)\s*\(\s*\2\s*\)/gi, '[$1]($2)')

  // 7) Rare case: bare URL followed later by the same URL again on the same line
  //    make it a single autolink: <https://x>
  s = s.replace(/\b(https?:\/\/[^\s)]+)\s*\(\s*\1\s*\)/g, '<$1>')

  // 8) Finally, if we still have bare www.* domains, wrap them in angle brackets so `marked` linkifies reliably
  s = s.replace(/(^|\s)(www\.[^\s<)]+)(?!\))/gi, '$1<https://$2>')

  return s
}

/**
 * Join a two-line pattern into one proper markdown link line.
 * Matches:
 *   Title: Description\n
 *   https://example.com(https://example.com)
 *   Title: Description\n
 *   <https://example.com>
 *   Title: Description\n
 *   https://example.com
 * Becomes:
 *   - [Title](https://example.com): Description
 */
function coalesceTitleDescPlusUrl(md: string): string {
  if (!md) return md
  const lines = md.split(/\n/)
  const out: string[] = []
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]
    // Title: Description (no URL on same line)
    const m = line.match(/^\s*([^:\n]{2,}?):\s*(.+?)\s*$/)
    if (m && i + 1 < lines.length) {
      const next = lines[i + 1]
      // Next line is a URL in one of several malformed forms
      const urlMatch = next.match(/^\s*<?\s*(https?:\/\/[^\s>]+)\s*>?\s*(?:\(\s*\1\s*\))?\s*$/)
      if (urlMatch) {
        const title = m[1].trim()
        const desc = m[2].trim()
        const url = urlMatch[1].trim()
        out.push(`- [${title}](${url})${desc ? `: ${desc}` : ''}`)
        i++ // consume the URL line
        continue
      }
    }
    out.push(line)
  }
  return out.join('\n')
}

/**
 * Join a two-line pattern into one markdown link when the first line is a bullet title
 * and the URL is on the next line, e.g.:
 *   * Title:
 *   https://example.com
 * ->
 *   * [Title](https://example.com)
 */
function coalesceBulletPlusUrlNextLine(md: string): string {
  if (!md) return md
  const lines = md.split(/\n/)
  const out: string[] = []
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]
    const m = line.match(/^(\s*[-*]\s+)([^:\n]{2,}?):\s*$/)
    if (m && i + 1 < lines.length) {
      const next = lines[i + 1]
      const urlMatch = next.match(/^\s*<?\s*(https?:\/\/[^\s>]+)\s*>?\s*(?:\(\s*\1\s*\))?\s*$/)
      if (urlMatch) {
        const bullet = m[1]
        const title = m[2].trim()
        const url = urlMatch[1].trim()
        out.push(`${bullet}[${title}](${url})`)
        i++
        continue
      }
    }
    out.push(line)
  }
  return out.join('\n')
}

/**
 * Join a two-line pattern into one markdown link when the first line is a title and
 * the URL is on the next line, e.g.:
 *   Title:
 *   https://example.com
 * ->
 *   [Title](https://example.com)
 */
function coalesceTitlePlusUrlNextLine(md: string): string {
  if (!md) return md
  const lines = md.split(/\n/)
  const out: string[] = []
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]
    const m = line.match(/^\s*([^:\n]{2,}?):\s*$/)
    if (m && i + 1 < lines.length) {
      const next = lines[i + 1]
      const urlMatch = next.match(/^\s*<?\s*(https?:\/\/[^\s>]+)\s*>?\s*(?:\(\s*\1\s*\))?\s*$/)
      if (urlMatch) {
        const title = m[1].trim()
        const url = urlMatch[1].trim()
        out.push(`[${title}](${url})`)
        i++
        continue
      }
    }
    out.push(line)
  }
  return out.join('\n')
}

/**
 * Turn lines like:
 *   * What are Agentic Workflows?: https://example.com
 *   * Title: `https://example.com`
 * into proper markdown links:
 *   * [What are Agentic Workflows?](https://example.com)
 */
function smartLinkifyBulletedReferences(md: string): string {
  if (!md) return md
  const lines = md.split(/\n/)
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]
    // bullet with title: "* Title: https://..." (url may be in backticks)
    const m = line.match(/^(\s*[-*]\s+)([^:\n]{2,}?):\s+`?(https?:\/\/[^\s`]+)`?\s*$/)
    if (m) {
      const bullet = m[1]
      const title = m[2].trim()
      const url = m[3].trim()
      lines[i] = `${bullet}[${title}](${url})`
      continue
    }
    // bullet with bare url only: "* https://..." -> angle-bracket so marked linkifies reliably
    const m2 = line.match(/^(\s*[-*]\s+)(https?:\/\/[^\s`]+)\s*$/)
    if (m2) {
      const bullet = m2[1]
      const url = m2[2].trim()
      lines[i] = `${bullet}<${url}>`
      continue
    }
  }
  return lines.join('\n')
}

/**
 * Convert non-bulleted colon refs like:
 *   Title: https://example.com
 *   Another Thing: `https://example.com/path`
 * into markdown links:
 *   [Title](https://example.com)
 */
function linkifyColonReferences(md: string): string {
  if (!md) return md
  const lines = md.split(/\n/)
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]

    // Skip if line already contains a markdown link
    if (/\[[^\]]+\]\(\s*https?:\/\/[^\s)]+\s*\)/.test(line)) continue

    // Match: "Title: https://..." (url may be surrounded by backticks)
    const m = line.match(/^(\s*)([^:\n]{2,}?):\s+`?(https?:\/\/[^\s`]+)`?\s*$/)
    if (m) {
      const indent = m[1] || ''
      const title = m[2].trim()
      const url = m[3].trim()
      lines[i] = `${indent}[${title}](${url})`
      continue
    }
  }
  return lines.join('\n')
}

/**
 * Deduplicate repeated paragraphs/blocks in a markdown string while preserving order.
 * Improved: splits into logical blocks (paragraphs, list items, headings), normalizes for minor streaming differences,
 * and keeps the first occurrence of each normalized block.
 */
function dedupeMarkdownParagraphs(md: string): string {
  if (!md) return md
  // Normalize zero-width characters first, then collapse intra-line repeats
  const cleaned = collapseIntraLineRepeats(md.replace(/[\u200B\u200C\u200D\u2060\uFEFF]/g, ''))
  // Split into logical "blocks": paragraphs (2+ newlines) or list items / headings
  const roughBlocks = cleaned
    .split(/\n{2,}/) // paragraphs
    .flatMap(block => {
      // further split blocks that contain many list items into per-line items
      const lines = block.split('\n')
      if (lines.length > 8) return lines // long lists tend to repeat per-line
      return [block]
    })
    .map(b => b.trim())
    .filter(Boolean)

  // Create a normalization that is resilient to tiny streaming differences
  const normalize = (s: string) => {
    return (
      s
        // normalize markdown links: [text](url) and bare links [https://...]
        .replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, '$1 $2')
        .replace(/\[https?:\/\/([^\]\s]+)\](?!\()/gi, 'https://$1')
        // collapse whitespace & punctuation variants
        .replace(/[ \t]+/g, ' ')
        .replace(/[\r]/g, '')
        .replace(/\s*\n\s*/g, '\n')
        .replace(/[,.;:!?]+$/g, '')
        // unify quotes and dashes
        .replace(/[’‘]/g, "'")
        .replace(/[“”]/g, '"')
        .replace(/[–—]/g, '-')
        // lowercase for comparison only
        .toLowerCase()
        .trim()
    )
  }

  const seen = new Set<string>()
  const out: string[] = []
  for (const raw of roughBlocks) {
    const key = normalize(raw)
    if (!key) continue
    if (seen.has(key)) continue
    seen.add(key)
    out.push(raw) // keep original form
  }

  // Reassemble with a single blank line between kept blocks
  return out.join('\n\n')
}

type Msg = {
  role: 'user' | 'model'
  text?: string
  html?: string
  kind?: 'thinking'
  extras?: {
    featuredLinks?: any[]
    nextQuestions?: string[]
  }
}

function extractEmbeddedJson(responseText: string) {
  try {
    let cleaned = (responseText || '').trim()
    function fromBlock(jsonText: string) {
      const parsed = JSON.parse(jsonText)
      return {
        text: parsed.text ?? '',
        featuredLinks: parsed.featuredLinks ?? [],
        sources: parsed.sources ?? [],
        NextQuestions: parsed.NextQuestions ?? [],
        relevantPages: parsed.relevantPages ?? [],
      }
    }

    try {
      return fromBlock(cleaned)
    } catch {
      let withoutBackticks = cleaned
        .replace(/^```json\s*/i, '')
        .replace(/\s*```$/g, '')
        .trim()
      try {
        return fromBlock(withoutBackticks)
      } catch {
        const startIdx = cleaned.indexOf('{')
        const endIdx = cleaned.lastIndexOf('}')
        if (startIdx !== -1 && endIdx !== -1 && endIdx > startIdx) {
          try {
            return fromBlock(cleaned.slice(startIdx, endIdx + 1).trim())
          } catch {}
        }
        const stripped = cleaned
          .replace(/^```(?:json)?\s*/i, '')
          .replace(/\s*```$/g, '')
          .trim()
        try {
          return fromBlock(stripped)
        } catch {
          const fallbackText = {
            text: cleaned
              .split(/\*\*(featuredLinks|sources|NextQuestions|relevantPages)\*\*:/i)[0]
              .trim(),
            featuredLinks: [...cleaned.matchAll(/\*\*sources\*\*:\s*\n((?:\*\s.*\n)+)/gi)].flatMap(
              (m: any) =>
                m[1]
                  .trim()
                  .split('\n')
                  .map((line: string) => line.replace(/^\*\s*/, ''))
            ),
            NextQuestions: [
              ...cleaned.matchAll(/\*\*NextQuestions\*\*:\s*\n((?:\*\s.*\n)+)/gi),
            ].flatMap((m: any) =>
              m[1]
                .trim()
                .split('\n')
                .map((line: string) => line.replace(/^\*\s*/, ''))
            ),
            relevantPages: [
              ...cleaned.matchAll(/\*\*relevantPages\*\*:\s*\n((?:\*\s.*\n)+)/gi),
            ].flatMap((m: any) =>
              m[1]
                .trim()
                .split('\n')
                .map((line: string) => line.replace(/^\*\s*/, ''))
            ),
          }
          return fallbackText
        }
      }
    }
  } catch {
    return {
      text: responseText,
      featuredLinks: [],
      sources: [],
      NextQuestions: [],
      relevantPages: [],
    }
  }
}

function ThinkingDots() {
  return (
    <span className="caption">
      Thinking{' '}
      <span className="typing-dots">
        <span className="dot dot1">.</span>
        <span className="dot dot2">.</span>
        <span className="dot dot3">.</span>
      </span>
    </span>
  )
}

const API = process.env.GATSBY_GEMINI_API_URL

const AskAi: React.FC = () => {
  const [popupOpen, setPopupOpen] = useState(false)
  const [fixedOpen, setFixedOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [input, setInput] = useState('')
  const [heroFixedInput, setHeroFixedInput] = useState('')
  const [messages, setMessages] = useState<Msg[]>([])
  const [hideNextQuestions, setHideNextQuestions] = useState(false)
  const chatRef = useRef<HTMLDivElement | null>(null)
  const lastMsgRef = useRef<HTMLDivElement | null>(null)
  const scrollBtnRef = useRef<HTMLButtonElement | null>(null)
  const ctrlRef = useRef<AbortController | null>(null)
  const streamTargetRef = useRef<number | null>(null)
  const metaLoadingIdxRef = useRef<number | null>(null)

  // Map of message index -> DOM node, so we can scroll a specific message to the top
  const msgRefs = useRef<Map<number, HTMLElement>>(new Map())

  // ---- scroll helpers for auto-hiding the "scroll to bottom" button
  const isAtBottom = (el: HTMLElement) => {
    const threshold = 8
    return el.scrollTop + el.clientHeight >= el.scrollHeight - threshold
  }

  const updateScrollButton = useCallback(() => {
    const el = chatRef.current
    const btn = scrollBtnRef.current
    if (!el || !btn) return
    const hasOverflow = el.scrollHeight > el.clientHeight + 1
    if (hasOverflow && !isAtBottom(el)) {
      btn.classList.add('visible')
    } else {
      btn.classList.remove('visible')
    }
  }, [])

  const scrollToBottom = useCallback(() => {
    const el = chatRef.current
    if (!el) return
    el.scrollTop = el.scrollHeight
    // re-evaluate button visibility on next frame
    requestAnimationFrame(updateScrollButton)
  }, [updateScrollButton])

  const scrollToLatestWithSpace = useCallback(() => {
    const el = chatRef.current
    if (!el) return
    const reserve = 160 // leave some empty space at the bottom for the incoming answer
    const target = Math.max(0, el.scrollHeight - el.clientHeight - reserve)
    el.scrollTo({ top: target, behavior: 'smooth' })
    // re-evaluate button visibility on next frame
    requestAnimationFrame(updateScrollButton)
  }, [updateScrollButton])

  const scrollMessageToTop = useCallback(
    (idx: number) => {
      const container = chatRef.current
      const node = msgRefs.current.get(idx)
      if (!container || !node) return

      // Compute the node's top relative to the scroll container, accounting for nested offsetParents
      const getRelativeTop = (el: HTMLElement, ancestor: HTMLElement) => {
        let top = 0
        let cur: HTMLElement | null = el
        while (cur && cur !== ancestor) {
          top += cur.offsetTop
          cur = cur.offsetParent as HTMLElement | null
        }
        return top
      }

      const pad = 8 // a tiny cushion so the previous message is fully off-screen
      const targetTop = Math.max(0, getRelativeTop(node, container) - pad)

      // Use scrollTo for the container; if the browser still scrolls the page, fall back to scrollIntoView on next frame
      container.scrollTo({ top: targetTop, behavior: 'instant' as ScrollBehavior })
      requestAnimationFrame(() => {
        // If our alignment didn't take (rare layout cases), ensure via scrollIntoView within nearest scrollable ancestor
        if (Math.abs(container.scrollTop - targetTop) > 1) {
          node.scrollIntoView({ block: 'start', inline: 'nearest' })
        }
        updateScrollButton()
      })
    },
    [updateScrollButton]
  )
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 0
  )
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth)
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])
  // dynamic placeholder
  const placeholderText = useMemo(() => {
    return windowWidth <= 420 ? 'Ask AI' : 'Ask AI about UiPath and agentic automation...'
  }, [windowWidth, popupOpen, fixedOpen])

  // keep the scroll button state correct (no auto-scroll on new messages)
  useEffect(() => {
    requestAnimationFrame(updateScrollButton)
  }, [messages, popupOpen, updateScrollButton])

  // attach scroll listener to toggle the scroll button visibility
  useEffect(() => {
    const el = chatRef.current
    if (!el) return

    const onScroll = () => updateScrollButton()
    el.addEventListener('scroll', onScroll)

    // instead of immediate call, delay a bit
    requestAnimationFrame(() => updateScrollButton())
    setTimeout(() => updateScrollButton(), 200) // covers async render

    return () => el.removeEventListener('scroll', onScroll)
  }, [messages, updateScrollButton])

  // add/remove blur style helper
  useEffect(() => {
    if (!isBrowser) return
    const styleTag = document.createElement('style')
    styleTag.textContent = `
      body.blur { position: relative; overflow: hidden !important; }
      body.blur::before { content:''; position: fixed; width:100%; height:100vh; box-shadow:0px 16px 32px -16px #0000007A; backdrop-filter: blur(4px); background:#293940B2; z-index:501; top:0; left:0; }
    body.scrollnone{position: relative; overflow: hidden !important;}
      `
    document.head.appendChild(styleTag)
    return () => {
      document.head.removeChild(styleTag)
    }
  }, [])

  // open popup from fixed cta
  const openFixedCta = useCallback(() => {
    setFixedOpen(true)
    if (isBrowser) document.body.classList.add('blur')
  }, [])

  const closeFixedCta = useCallback(() => {
    setFixedOpen(false)
    setHeroFixedInput('')
    if (isBrowser) document.body.classList.remove('blur')
  }, [])

  const openPopup = useCallback(() => {
    setPopupOpen(true)
    setFixedOpen(false)
    if (isBrowser) document.body.classList.remove('blur')
    if (isBrowser) document.body.classList.add('scrollnone')
    ensureMarkdownLibs().catch(() => {})
    // ensure correct initial placement and scroll state
    requestAnimationFrame(updateScrollButton)
  }, [])

  const closePopup = useCallback(() => {
    setPopupOpen(false)
    setMessages([])
    setHeroFixedInput('')
    if (ctrlRef.current) ctrlRef.current.abort()
    if (isBrowser) document.body.classList.remove('scrollnone')
  }, [])

  // clear all chat state and reset UI
  const clearChat = useCallback(() => {
    // cancel any in-flight requests
    try {
      ctrlRef.current?.abort()
    } catch {}
    // reset refs that might affect next turn placement
    streamTargetRef.current = null
    metaLoadingIdxRef.current = null

    // reset UI state
    setMessages([])
    setInput('')
    setHeroFixedInput('')
    setBusy(false)
    setShowSources(false)
    setHideNextQuestions(false)

    // clear any persisted history (if used)
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        window.localStorage.removeItem('chatHistory')
      }
    } catch {}

    // scroll chat container to the very top and refresh scroll button
    requestAnimationFrame(() => {
      const c = chatRef.current
      if (c) c.scrollTop = 0
      requestAnimationFrame(updateScrollButton)
    })
  }, [updateScrollButton])

  const addUser = useCallback((text: string) => {
    setMessages(m => [...m, { role: 'user', text }])
  }, [])
  const addThinking = useCallback(() => {
    setMessages(m => [...m, { role: 'model', kind: 'thinking' }])
  }, [])
  const replaceLastModelHTML = useCallback((html: string, targetIdx?: number) => {
    setMessages(m => {
      let idx =
        typeof targetIdx === 'number' &&
        targetIdx >= 0 &&
        targetIdx < m.length &&
        m[targetIdx]?.role === 'model'
          ? targetIdx
          : -1

      // fallback: last model message if no valid targetIdx
      if (idx === -1) {
        for (let i = m.length - 1; i >= 0; i--) {
          if (m[i].role === 'model') {
            idx = i
            break
          }
        }
      }

      if (idx === -1) return [...m, { role: 'model', html }]
      const copy = m.slice()
      copy[idx] = { role: 'model', html }
      return copy
    })
  }, [])

  const handleSend = useCallback(
    async (forced?: string) => {
      await ensureMarkdownLibs().catch(() => {})
      const q = (forced ?? input).trim()
      if (!q || busy) return
      const clientId = getGAClientId()

      const shortQ = q
        .replace(/^How do I agentify\s*\??\s*/i, '') // remove prefix
        .replace(/\?$/, '') // remove ending ?

      // ✅ send to GA
      if (typeof window !== 'undefined' && (window as any).dataLayer) {
        ;(window as any).dataLayer.push({
          event: 'ask_ai_question',
          question: q,
          pageUrl: window.location?.href || '',
          clickButton: shortQ,
          clientId,
        })
      }

      setBusy(true)
      setInput('')
      // compute target index for this turn's streaming message
      const baseLen = messages.length
      addUser(q)
      addThinking()
      streamTargetRef.current = baseLen + 1
      // Scroll so the just-added user message sits at the very top of the chat viewport
      requestAnimationFrame(() => scrollMessageToTop(baseLen))

      // Build up to 5 previous Q/A pairs
      const prevPairs: { question: string; answer: string }[] = []
      for (let i = messages.length - 1; i >= 0 && prevPairs.length < 5; ) {
        const ans = messages[i]
        if (ans?.role === 'model' && ans.html) {
          let j = i - 1
          while (j >= 0 && messages[j].role !== 'user') j--
          if (j >= 0) {
            prevPairs.push({
              question: messages[j]?.text ?? '',
              answer: ans.html,
            })
            i = j - 1
            continue
          }
        }
        i--
      }

      const prevContext: any[] = []
      for (let k = prevPairs.length - 1; k >= 0; k--) {
        const pair = prevPairs[k]
        prevContext.push({
          role: 'user',
          parts: [
            { text: `Previous Question: ${pair.question.replace(/^Previous Question:\s*/i, '')}` },
          ],
        })
        prevContext.push({
          role: 'model',
          parts: [{ text: `Previous Answer: ${pair.answer.replace(/^Previous Answer:\s*/i, '')}` }],
        })
      }

      const requestBody = {
        query: [...prevContext, { role: 'user', parts: [{ text: q }] }],
        version: 'v3',
      }

      // meta
      const metaPromise = fetch(API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...requestBody,
          format: 'json',
          pageUrl: window.location.href,
          clientId,
        }),
      })
        .then(r => r.json())
        .catch(() => null)

      // stream
      ctrlRef.current?.abort()
      const controller = new AbortController()
      ctrlRef.current = controller

      try {
        const res = await fetch(API, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          signal: controller.signal,
          body: JSON.stringify({
            ...requestBody,
            pageUrl: window.location.href,
          }),
        })

        const reader = res.body?.getReader()
        const decoder = new TextDecoder()
        let buffer = ''
        let firstChunk = true
        let mdBuffer = ''
        let receivedText = false
        let sawStop = false
        // --- streaming sources accumulator ---
        const streamSources: { url: string; title: string; description: string }[] = []
        const pushStreamSource = (rc: any) => {
          if (!rc) return
          const url: string = rc.uri || rc.url || rc.link || ''
          const title: string = rc.title || (url ? url.replace(/^https?:\/\//, '') : '')
          const description: string = (rc.text || rc.snippet || rc.summary || '').toString()
          if (!url && !title) return
          const key = (title + '|' + url).toLowerCase()
          if (streamSources.some(s => (s.title + '|' + s.url).toLowerCase() === key)) return
          streamSources.push({ url: url || '#', title, description })
        }

        if (reader) {
          while (true) {
            const { done, value } = await reader.read()
            if (done) break

            let chunkText = decoder.decode(value, { stream: true }).trim()
            if (firstChunk) {
              chunkText = chunkText.replace(/^\[/, '')
              firstChunk = false
            }
            if (!buffer && chunkText.startsWith(',')) chunkText = chunkText.slice(1).trim()
            buffer += chunkText

            let safeBuffer = buffer.trim().replace(/^\s*,\s*/g, '')
            // pull JSON objects from NDJSON-esque stream
            while (true) {
              let endIndex = 0,
                openBraces = 0,
                inString = false
              for (let i = 0; i < safeBuffer.length; i++) {
                const ch = safeBuffer[i]
                if (ch === '"' && safeBuffer[i - 1] !== '\\') inString = !inString
                if (!inString) {
                  if (ch === '{') openBraces++
                  else if (ch === '}') openBraces--
                }
                if (openBraces === 0 && i > 0) {
                  endIndex = i + 1
                  break
                }
              }
              if (endIndex === 0) break

              const candidate = safeBuffer.slice(0, endIndex)
              try {
                const parsed = JSON.parse(candidate)
                // collect grounding sources from streamed chunk
                const gm = parsed?.candidates?.[0]?.groundingMetadata
                const chunks = (gm?.groundingChunks as any[]) || []
                for (const ch of chunks) {
                  const rc = ch?.retrievedContext
                  if (rc) pushStreamSource(rc)
                }
                const partsArr = (parsed?.candidates?.[0]?.content?.parts as any[]) || []
                const fullText = partsArr.map((p: any) => p?.text || '').join('')

                if (sawStop) {
                  safeBuffer = safeBuffer.slice(endIndex).trimStart()
                  if (safeBuffer.startsWith(',')) safeBuffer = safeBuffer.slice(1).trimStart()
                  buffer = safeBuffer
                  continue
                }

                const chunkMd = fullText.replace(/UiPath['’]s/g, 'UiPath')
                // cumulative / delta merge (de-dupe)
                if (chunkMd) {
                  if (!receivedText) receivedText = true
                  mdBuffer = mergeChunk(mdBuffer, chunkMd)
                  const liveDeduped = dedupeMarkdownParagraphs(mdBuffer)
                  const liveClean = collapseIntraLineRepeats(liveDeduped)
                  const liveFixed = fixMalformedLinks(liveClean)
                  const liveCoalesced = coalesceTitleDescPlusUrl(liveFixed)
                  const liveTitleNext = coalesceTitlePlusUrlNextLine(liveCoalesced)
                  const liveBulletNext = coalesceBulletPlusUrlNextLine(liveTitleNext)
                  const liveColon = linkifyColonReferences(liveBulletNext)
                  const liveLinked = smartLinkifyBulletedReferences(liveColon)
                  replaceLastModelHTML(
                    renderMarkdownToHTML(liveLinked),
                    streamTargetRef.current ?? undefined
                  )
                }

                const stopCandidate = parsed?.candidates?.find(
                  (c: any) => c.finishReason === 'STOP'
                )
                if (stopCandidate) {
                  sawStop = true
                }

                safeBuffer = safeBuffer.slice(endIndex).trimStart()
                if (safeBuffer.startsWith(',')) safeBuffer = safeBuffer.slice(1).trimStart()
                buffer = safeBuffer
              } catch {
                break
              }
            }
          }
        }

        // collapse duplicate tail (your heuristic)
        const collapseDuplicateTail = (s?: string) => {
          if (!s || s.length < 2000) return s || ''
          const anchorA = s.slice(0, 300)
          const lastA = s.lastIndexOf(anchorA)
          if (lastA > 500) return s.slice(0, lastA)
          const midStart = Math.floor(s.length * 0.2)
          const anchorB = s.slice(midStart, midStart + 220)
          const lastB = s.lastIndexOf(anchorB)
          if (lastB > midStart + 220) return s.slice(0, lastB)
          const TAIL = 800
          if (s.length > TAIL * 2) {
            const tail = s.slice(-TAIL)
            const firstIdx = s.indexOf(tail)
            const lastIdx = s.lastIndexOf(tail)
            if (firstIdx !== -1 && firstIdx < lastIdx) return s.slice(0, lastIdx)
          }
          return s
        }
        const finalMd = collapseDuplicateTail(mdBuffer)
        if (finalMd) {
          const finalDeduped = dedupeMarkdownParagraphs(finalMd)
          const finalClean = collapseIntraLineRepeats(finalDeduped)
          const finalFixed = fixMalformedLinks(finalClean)
          const finalCoalesced = coalesceTitleDescPlusUrl(finalFixed)
          const finalTitleNext = coalesceTitlePlusUrlNextLine(finalCoalesced)
          const finalBulletNext = coalesceBulletPlusUrlNextLine(finalTitleNext)
          const finalColon = linkifyColonReferences(finalBulletNext)
          const finalLinked = smartLinkifyBulletedReferences(finalColon)
          replaceLastModelHTML(
            renderMarkdownToHTML(finalLinked),
            streamTargetRef.current ?? undefined
          )
        }
        // attach any streamed sources (title + description) to this turn
        if (streamSources.length) {
          const target = streamTargetRef.current
          setMessages(prev => {
            const copy = prev.slice()
            const idx =
              typeof target === 'number' && target >= 0 && target < copy.length
                ? target
                : copy.length - 1
            const prevExtras: any = (copy[idx] as any).extras || {}
            const existing: any[] = Array.isArray(prevExtras.sources) ? prevExtras.sources : []
            const merged = [...existing]
            for (const s of streamSources) {
              const key = (s.title + '|' + s.url).toLowerCase()
              if (!merged.some(m => (m.title + '|' + m.url).toLowerCase() === key)) {
                merged.push(s)
              }
            }
            copy[idx] = { ...copy[idx], extras: { ...prevExtras, sources: merged } }
            return copy
          })
        }
        // While meta (featured links / next questions) is pending, show a thinking bubble
        setMessages(prev => {
          const arr = [...prev, { role: 'model', kind: 'thinking' as const }]
          metaLoadingIdxRef.current = arr.length - 1
          return arr
        })

        // meta -> extras (next questions + links)
        try {
          const meta = await metaPromise
          if (meta) {
            const parts = meta?.candidates?.[0]?.content?.parts || []
            const respText = parts.map((p: any) => p.text).join('')
            const parsed = extractEmbeddedJson(respText)
            const links = Array.isArray(parsed?.featuredLinks) ? parsed.featuredLinks : []
            const sources = Array.isArray((parsed as any)?.sources) ? (parsed as any).sources : []
            const parsedNext =
              (parsed as any)?.NextQuestions || (parsed as any)?.nextQuestions || []
            const nextQs =
              Array.isArray(parsedNext) && parsedNext.length
                ? parsedNext
                : [
                    'How do UiPath AI Agents work?',
                    'What are the benefits of agentic automation?',
                    'How can I build my own AI agents with UiPath?',
                    'What industries benefit most from agentic automation?',
                  ]
            // remove the temporary meta loading bubble, if present
            setMessages(prev => {
              const out = [...prev]
              const idx = metaLoadingIdxRef.current
              if (
                idx !== null &&
                idx >= 0 &&
                idx < out.length &&
                out[idx]?.role === 'model' &&
                (out[idx] as any).kind === 'thinking'
              ) {
                out.splice(idx, 1)
              }
              metaLoadingIdxRef.current = null
              return out
            })
            // Attach extras to the correct bot message (this turn only)
            const target = streamTargetRef.current
            setMessages(m => {
              const copy = m.slice()
              const idx =
                typeof target === 'number' && target >= 0 && target < copy.length
                  ? target
                  : copy.length - 1
              if (copy[idx] && copy[idx].role === 'model') {
                const prevExtras: any = (copy[idx] as any).extras || {}
                const existingSources: any[] = Array.isArray(prevExtras.sources)
                  ? prevExtras.sources
                  : []
                const metaSources: any[] = Array.isArray(sources) ? sources : []
                const mergedSources = [...existingSources]
                for (const s of metaSources) {
                  const t = s?.title || s?.name || ''
                  const u = s?.url || s?.link || ''
                  const key = (t + '|' + u).toLowerCase()
                  if (
                    !mergedSources.some(
                      m =>
                        (
                          (m.title || m.name || '') +
                          '|' +
                          (m.url || m.link || '')
                        ).toLowerCase() === key
                    )
                  ) {
                    mergedSources.push({
                      url: u || '#',
                      title: t || (u ? u.replace(/^https?:\/\//, '') : ''),
                      description: s?.description || s?.snippet || s?.summary || '',
                    })
                  }
                }
                copy[idx] = {
                  ...copy[idx],
                  extras: {
                    ...prevExtras,
                    featuredLinks: links,
                    nextQuestions: nextQs,
                    sources: mergedSources,
                  },
                }
              }
              return copy
            })
            setHideNextQuestions(false)
          }
        } catch {
          // nothing to do for fallback
        }
      } catch (err) {
        // ensure meta loading bubble is cleared on error
        setMessages(prev => {
          const out = [...prev]
          const idx = metaLoadingIdxRef.current
          if (
            idx !== null &&
            idx >= 0 &&
            idx < out.length &&
            out[idx]?.role === 'model' &&
            (out[idx] as any).kind === 'thinking'
          ) {
            out.splice(idx, 1)
          }
          metaLoadingIdxRef.current = null
          return out
        })
        replaceLastModelHTML(renderMarkdownToHTML(''))
        setHideNextQuestions(false)
      } finally {
        // defensive: clear any stray meta loading bubble
        setMessages(prev => {
          const out = [...prev]
          const idx = metaLoadingIdxRef.current
          if (
            idx !== null &&
            idx >= 0 &&
            idx < out.length &&
            out[idx]?.role === 'model' &&
            (out[idx] as any).kind === 'thinking'
          ) {
            out.splice(idx, 1)
          }
          metaLoadingIdxRef.current = null
          return out
        })
        setBusy(false)
        setHideNextQuestions(false)
      }
    },
    [input, busy, messages, addUser, addThinking, replaceLastModelHTML, scrollMessageToTop]
  )

  // ---- public bridge so plain HTML can trigger the React popup ----
  useEffect(() => {
    // expose a minimal API for the host page (HTML widget) to call
    window.askAiBridge = {
      open: () => {
        openPopup()
        // align scroll UI on next frame
        requestAnimationFrame(updateScrollButton)
      },
      ask: (query: string) => {
        const q = (query || '').trim()
        if (!q) return
        // open first to mount modal, then send to avoid race on first paint
        openPopup()
        setTimeout(() => handleSend(q), 0)
      },
    }

    return () => {
      // cleanup when component unmounts/reloads
      try {
        delete (window as any).askAiBridge
      } catch {}
    }
  }, [openPopup, handleSend, updateScrollButton])

  // helper to send from fixed CTA
  const sendFromFixed = useCallback(
    async (qRaw: string) => {
      const q = (qRaw || '').trim()
      if (!q) return

      // reflect in fixed input immediately
      setHeroFixedInput(q)
      // open the main modal (this also removes blur and preps libs)
      openPopup()
      // ensure correct initial placement and scroll state
      requestAnimationFrame(updateScrollButton)
      // just in case: ensure libs are ready before first render
      await ensureMarkdownLibs().catch(() => {})
      // set chat input and send
      setInput(q)
      setTimeout(() => handleSend(q), 0)
    },
    [handleSend, openPopup]
  )

  const handleHeroFixedSubmit = useCallback(() => {
    if (!heroFixedInput.trim()) return
    // open popup and preload input
    setInput(heroFixedInput.trim())
    openPopup()
    requestAnimationFrame(updateScrollButton)
    setTimeout(() => {
      handleSend(heroFixedInput.trim())
    }, 0)
    setHeroFixedInput('')
  }, [heroFixedInput, openPopup])

  // UI pieces
  const LinksBlock: React.FC<{ links: any[] }> = ({ links }) => {
    if (!links || !links.length) return null
    const valid = links.filter(q => q.title && q.title.trim() !== '').slice(0, 4)
    if (!valid.length) return null
    return (
      <div className="commonData">
        <div className="top__heading--panel">
          <p className="body-S heading__tag">Want to dive deeper?</p>
          <div className="resource--link">
            <a
              href="https://www.uipath.com/resources/all"
              target="_blank"
              rel="noopener noreferrer"
              className="body-S link--resource ga-linkResource">
              View more resources
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="17"
                viewBox="0 0 16 17"
                fill="none">
                <path
                  d="M7.86269 12.5355L8.80541 13.4782L13.943 8.34068L8.80541 3.20312L7.86269 4.14584L11.391 7.67413H2.00134V9.00733H11.3909L7.86269 12.5355Z"
                  fill="#FA4616"
                />
              </svg>
            </a>
          </div>
        </div>
        <div className="items__row">
          {valid.map((q, i) => {
            const category = (q.category || 'Resources') as string
            const safeCategory = category.trim().toLowerCase().replace(/\s+/g, '')
            return (
              <div className="item--div" key={i}>
                <a
                  href={q.url || '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ga-diveDeeperLink item__link"
                  aria-label={q.title}>
                  {category}
                </a>

                <div className="image--div">
                  <h4 className={`category caption-alt ${safeCategory}`}>{category}</h4>
                </div>
                <h3 className="caption">{q.title}</h3>
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  const NextQuestionsBlock: React.FC<{
    items: string[]
    onAsk: (q: string) => void
    disabled?: boolean
  }> = ({ items, onAsk, disabled }) => {
    if (!items || !items.length) return null
    return (
      <div
        id="nextQuestionsWrapper"
        style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginTop: 8 }}>
        <p className="body-S heading__tag" style={{ width: '100%' }}>
          Explore more questions
        </p>
        {items.map((raw, i) => {
          const cleaned = raw.replace(/UiPath's/g, 'UiPath')
          return (
            <button
              key={i}
              className="ga-nextQuestion chat-chip"
              disabled={!!disabled}
              onClick={() => onAsk(cleaned)}>
              → {cleaned}
            </button>
          )
        })}
      </div>
    )
  }

  const FeedbackAndCTA: React.FC<{
    modelIdx: number
    sourcesAvailable?: boolean
    showSources?: boolean
    onToggleSources?: () => void
  }> = ({ modelIdx, sourcesAvailable, showSources, onToggleSources }) => {
    const [status, setStatus] = useState<'up' | 'down' | null>(null)
    const handleClick = useCallback(
      (kind: 'up' | 'down') => {
        setStatus(kind) // update UI first
        sendFeedback(kind, modelIdx) // call your API
      },
      [modelIdx, sendFeedback]
    )
    return (
      <div className="bottom-part">
        {/* top row: helpful + sources toggle on the right */}
        <div className="main__thumb--row" style={{ alignItems: 'center' }}>
          <div className="thumbSection">
            <p className="body-S thumbText">Was this helpful?</p>
            {status !== 'down' && (
              <button
                type="button"
                className={status === 'up' ? 'thumb thumb-up-liked' : 'thumb thumb-up'}
                title="Yes, helpful"
                onClick={() => handleClick('up')}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none">
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M7.88966 7.66693L9.22558 4.10447C9.48705 3.40722 9.11594 2.67209 8.47534 2.42303L4.99941 7.63692V13.6669H12.5513L13.9476 8.08327C14.0002 7.87295 13.8411 7.66919 13.6244 7.66911L7.88966 7.66693ZM13.3319 14.6668H3.99951V7.33418L7.99911 1.33478C9.61181 1.33478 10.7281 2.94555 10.1618 4.45556L9.33231 6.66758L13.6247 6.66921C14.4919 6.66954 15.128 7.48458 14.9176 8.32585L13.3319 14.6668Z"
                    fill=""
                  />
                  <path
                    fillRule="evenodd"
                    clip-rule="evenodd"
                    d="M1.5 14.6674V7.33417H2.4999V14.6674H1.5Z"
                    fill=""
                  />
                </svg>
              </button>
            )}
            {status !== 'up' && (
              <button
                type="button"
                className={status === 'down' ? 'thumb thumb-down-unliked' : 'thumb thumb-down'}
                title="No, not helpful"
                onClick={() => handleClick('down')}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none">
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M7.8882 8.33466L9.22412 11.8971C9.48559 12.5944 9.11447 13.3295 8.47388 13.5786L4.99795 8.36466V2.33471H12.5498L13.9461 7.91832C13.9987 8.12863 13.8397 8.33239 13.6229 8.33248L7.8882 8.33466ZM13.3304 1.33481H3.99805V8.66741L7.99765 14.6668C9.61034 14.6668 10.7266 13.056 10.1604 11.546L9.33085 9.33401L13.6233 9.33238C14.4904 9.33205 15.1265 8.517 14.9161 7.67574L13.3304 1.33481Z"
                    fill=""
                  />
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M1.49854 1.33423V8.66742H2.49844V1.33423H1.49854Z"
                    fill=""
                  />
                </svg>
              </button>
            )}
          </div>

          {sourcesAvailable ? (
            <button
              type="button"
              className="toggle__Source ga-toggleSource"
              onClick={onToggleSources}
              aria-label={
                showSources ? 'Hide sources and references' : 'Show sources and references'
              }
              style={{ marginLeft: 'auto' }}>
              <span className="svg--icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none">
                  <path
                    d="M11.3339 6.00019C12.4383 6.00019 13.3337 6.89553 13.3337 7.99999C13.3337 9.10445 12.4383 9.99979 11.3339 9.99979H8.66748V11.333H11.3339C13.1746 11.333 14.6669 9.84076 14.6669 7.99999C14.6669 6.15923 13.1746 4.66699 11.3339 4.66699H8.66748V6.00019H11.3339Z"
                    fill="#FA4616"
                  />
                  <path
                    d="M4.66747 6.00019H7.33387V4.66699H4.66747C2.82671 4.66699 1.33447 6.15923 1.33447 7.99999C1.33447 9.84076 2.82671 11.333 4.66747 11.333H7.33387V9.99979H4.66747C3.56301 9.99979 2.66767 9.10445 2.66767 7.99999C2.66767 6.89553 3.56301 6.00019 4.66747 6.00019Z"
                    fill="#FA4616"
                  />
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M10.0006 8.6667H6.00098V7.3335H10.0006V8.6667Z"
                    fill="#FA4616"
                  />
                </svg>
              </span>
              <span className="caption">{showSources ? 'Hide sources' : 'Show sources'}</span>
            </button>
          ) : null}
        </div>

        {/* CTA panel */}
        <div className="contactUs-section">
          <p className="body-M contactText">Ready to take the next step?</p>
          <a
            className="linking commnonBtn text-link-primary ga-tryItBtn"
            href="https://cloud.uipath.com/portal_/register?subscriptionPlan=std-trial"
            target="_blank"
            rel="noopener noreferrer">
            Try it yourself
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none">
              <path d="M7.97893 12.3126L8.68597 13.0197L13.7057 7.99994L8.68597 2.98022L7.97893 3.68726L11.391 7.5H2v1h9.391L7.97893 12.3126Z" />
            </svg>
          </a>
          <a
            className="linking commnonBtn text-link-primary ga-contactUs"
            href="https://www.uipath.com/company/contact-us"
            target="_blank"
            rel="noopener noreferrer">
            Connect with our team
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none">
              <path d="M7.97893 12.3126L8.68597 13.0197L13.7057 7.99994L8.68597 2.98022L7.97893 3.68726L11.391 7.5H2v1h9.391L7.97893 12.3126Z" />
            </svg>
          </a>
        </div>
      </div>
    )
  }

  const lastModelIndex = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === 'model' && !messages[i].kind) return i
    }
    return -1
  }, [messages])

  // When the last message is a thinking bubble, add extra bottom space so it can scroll to the very top
  const needsBottomSpacer = useMemo(() => {
    const last = messages[messages.length - 1]
    return !!(last && last.role === 'model' && last.kind === 'thinking')
  }, [messages])

  // Helper to check if any meta is present (featuredLinks, nextQuestions, sources)
  const hasAnyMeta = useCallback((m: Msg | undefined) => {
    if (!m) return false
    const ex: any = (m as any).extras || {}
    const hasLinks = Array.isArray(ex.featuredLinks) && ex.featuredLinks.length > 0
    const hasNext = Array.isArray(ex.nextQuestions) && ex.nextQuestions.length > 0
    const hasSources = Array.isArray(ex.sources) && ex.sources.length > 0
    return hasLinks || hasNext || hasSources
  }, [])

  // --- feedback helpers (uses latest model + its preceding user message) ---
  const getPrevUserText = useCallback(
    (modelIdx: number) => {
      for (let j = modelIdx - 1; j >= 0; j--) {
        if (messages[j]?.role === 'user') return messages[j]?.text || ''
      }
      return ''
    },
    [messages]
  )

  const sendFeedback = useCallback(
    async (kind: 'up' | 'down', modelIdx: number) => {
      try {
        const question = getPrevUserText(modelIdx)
        const answerHtml = messages[modelIdx]?.html || ''
        const clientId = getGAClientId()

        // ✅ Push feedback to GA dataLayer
        try {
          if (typeof window !== 'undefined' && (window as any).dataLayer) {
            // Strip HTML to a short snippet for analytics
            let answerSnippet = ''
            try {
              const div = document.createElement('div')
              div.innerHTML = answerHtml
              const txt = (div.textContent || div.innerText || '').trim()
              answerSnippet = txt.slice(0, 300)
            } catch {}
            ;(window as any).dataLayer.push({
              event: 'ask_ai_feedback',
              feedback: kind, // 'up' | 'down'
              question, // the user's question for this answer
              modelIndex: modelIdx, // index of the model message
              pageUrl: window.location?.href || '',
              clientId,
            })
          }
        } catch {}

        await fetch(API, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            type: 'feedback',
            feedback: kind,
            question,
            answer: answerHtml,
            pageUrl: window.location.href,
            clientId: clientId,
          }),
        })
        console.log(kind, modelIdx, 'adasd')
      } catch {
        // ignore network errors
      }
    },
    [messages, getPrevUserText]
  )

  // Sources panel state
  const [showSources, setShowSources] = useState(false)

  // AI disclaimer tooltip (info bubble near the title)
  const [showDisclaimer, setShowDisclaimer] = useState(false)
  const disclaimerRef = useRef<HTMLDivElement | null>(null)

  // Derive sources from the latest model message (prefer explicit sources, else featuredLinks)
  const currentSources = useMemo(() => {
    if (lastModelIndex === -1) return [] as any[]
    const m = messages[lastModelIndex]

    // Helper to normalize different shapes coming from the API/meta
    const norm = (q: any) => {
      const url = q?.url || q?.link || q?.href || ''
      const title = q?.title || q?.name || (url ? url.replace(/^https?:\/\//, '') : '')
      const description = q?.description || q?.snippet || q?.summary || q?.excerpt || q?.text || ''
      return { url: url || '#', title, description }
    }

    const explicit = (m as any)?.extras?.sources
    if (Array.isArray(explicit) && explicit.length) {
      return explicit.filter((q: any) => q && (q.title || q.name || q.url || q.link)).map(norm)
    }

    const links = (m as any)?.extras?.featuredLinks || []
    return Array.isArray(links)
      ? links.filter((q: any) => q && (q.title || q.name || q.url || q.link)).map(norm)
      : []
  }, [messages, lastModelIndex])

  // Ensure scroll button updates when showSources changes
  useEffect(() => {
    requestAnimationFrame(updateScrollButton)
  }, [showSources, updateScrollButton])

  // Close the disclaimer when clicking outside or pressing Escape
  useEffect(() => {
    if (!showDisclaimer) return
    const onDocClick = (e: MouseEvent) => {
      const el = disclaimerRef.current
      if (el && !el.contains(e.target as Node)) {
        setShowDisclaimer(false)
      }
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowDisclaimer(false)
    }
    document.addEventListener('mousedown', onDocClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDocClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [showDisclaimer])

  return (
    <>
      {/* ******** fixed search start ******** */}
      <div
        className="fixed__search__cta slideInRight"
        onClick={openFixedCta}
        role="button"
        tabIndex={0}>
        <span className="cta">
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="ai__icon">
            <path
              d="M16.602 11.1769C12.6462 10.5066 9.52469 7.2136 8.88938 3.04052C8.8774 2.96175 8.78064 2.96175 8.76865 3.04052C8.13335 7.2136 5.0118 10.5066 1.05603 11.1769C0.981323 11.1896 0.981323 11.2917 1.05603 11.3043C5.0118 11.9745 8.13335 15.2677 8.76865 19.4407C8.78064 19.5195 8.87741 19.5195 8.88938 19.4407C9.52469 15.2677 12.6462 11.9745 16.602 11.3043C16.6767 11.2917 16.6767 11.1896 16.602 11.1769ZM12.7155 11.2725C10.7376 11.6076 9.17685 13.2541 8.8592 15.3407C8.85321 15.3801 8.80482 15.3801 8.79883 15.3407C8.48118 13.2541 6.9204 11.6076 4.94252 11.2725C4.90517 11.2662 4.90517 11.2151 4.94252 11.2088C6.9204 10.8736 8.48118 9.22711 8.79883 7.14057C8.80482 7.10119 8.85321 7.10119 8.8592 7.14057C9.17685 9.22711 10.7376 10.8736 12.7155 11.2088C12.7529 11.2151 12.7529 11.2662 12.7155 11.2725Z"
              fill="#FA4616"
            />
            <path
              d="M18.9719 4.66145C16.994 4.99652 15.4332 6.64311 15.1156 8.72965C15.1096 8.76903 15.0612 8.76903 15.0552 8.72965C14.7376 6.64311 13.1768 4.99652 11.1989 4.66145C11.1616 4.65513 11.1616 4.60406 11.1989 4.59774C13.1768 4.2626 14.7376 2.61608 15.0552 0.529537C15.0612 0.490154 15.1096 0.490154 15.1156 0.529537C15.4332 2.61608 16.994 4.2626 18.9719 4.59774C19.0092 4.60406 19.0092 4.65513 18.9719 4.66145Z"
              fill="#FA4616"
            />
          </svg>
          <span className="body-L">Ask AI about...</span>
        </span>
      </div>

      {fixedOpen && (
        <div className="input__design__fixed slideInRight">
          <div className="hero__search theme-dark">
            <div className="flex__container">
              <div className="input__design">
                <input
                  id="hero__ai__input__fixed"
                  type="text"
                  placeholder={placeholderText}
                  value={heroFixedInput}
                  onChange={e => setHeroFixedInput(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      sendFromFixed(heroFixedInput)
                    }
                  }}
                />
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="ai__icon">
                  <path
                    d="M16.602 11.1769C12.6462 10.5066 9.52469 7.2136 8.88938 3.04052C8.8774 2.96175 8.78064 2.96175 8.76865 3.04052C8.13335 7.2136 5.0118 10.5066 1.05603 11.1769C0.981323 11.1896 0.981323 11.2917 1.05603 11.3043C5.0118 11.9745 8.13335 15.2677 8.76865 19.4407C8.78064 19.5195 8.87741 19.5195 8.88938 19.4407C9.52469 15.2677 12.6462 11.9745 16.602 11.3043C16.6767 11.2917 16.6767 11.1896 16.602 11.1769ZM12.7155 11.2725C10.7376 11.6076 9.17685 13.2541 8.8592 15.3407C8.85321 15.3801 8.80482 15.3801 8.79883 15.3407C8.48118 13.2541 6.9204 11.6076 4.94252 11.2725C4.90517 11.2662 4.90517 11.2151 4.94252 11.2088C6.9204 10.8736 8.48118 9.22711 8.79883 7.14057C8.80482 7.10119 8.85321 7.10119 8.8592 7.14057C9.17685 9.22711 10.7376 10.8736 12.7155 11.2088C12.7529 11.2151 12.7529 11.2662 12.7155 11.2725Z"
                    fill="#FA4616"
                  />
                  <path
                    d="M18.9719 4.66145C16.994 4.99652 15.4332 6.64311 15.1156 8.72965C15.1096 8.76903 15.0612 8.76903 15.0552 8.72965C14.7376 6.64311 13.1768 4.99652 11.1989 4.66145C11.1616 4.65513 11.1616 4.60406 11.1989 4.59774C13.1768 4.2626 14.7376 2.61608 15.0552 0.529537C15.0612 0.490154 15.1096 0.490154 15.1156 0.529537C15.4332 2.61608 16.994 4.2626 18.9719 4.59774C19.0092 4.60406 19.0092 4.65513 18.9719 4.66145Z"
                    fill="#FA4616"
                  />
                </svg>
                <div className="submit__design">
                  <button
                    type="button"
                    id="hero__ai__fixed"
                    disabled={!heroFixedInput.trim()}
                    onClick={() => sendFromFixed(heroFixedInput)}>
                    <span>
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg">
                        <path
                          d="M2.6665 8.00033L3.6065 8.94033L7.33317 5.22033V13.3337H8.6665V5.22033L12.3865 8.94699L13.3332 8.00033L7.99984 2.66699L2.6665 8.00033Z"
                          fill="#F8F9FA"
                        />
                      </svg>
                    </span>
                  </button>
                </div>
              </div>
              <button className="close__btn" onClick={closeFixedCta} aria-label="Close">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg">
                  <rect x="1" y="1" width="18" height="18" rx="4" fill="white" />
                  <rect
                    x="1"
                    y="1"
                    width="18"
                    height="18"
                    rx="4"
                    stroke="#293940"
                    strokeWidth="2"
                  />
                  <path
                    d="M5.33317 15.8337L4.1665 14.667L8.83317 10.0003L4.1665 5.33366L5.33317 4.16699L9.99984 8.83366L14.6665 4.16699L15.8332 5.33366L11.1665 10.0003L15.8332 14.667L14.6665 15.8337L9.99984 11.167L5.33317 15.8337Z"
                    fill="#526069"
                  />
                </svg>
              </button>
            </div>
            <p className="body-S">
              Not sure where to start? Find out how to agentify any enterprise:
            </p>
            <ul>
              {['Financial Services', 'Healthcare', 'Manufacturing', 'Public Sector'].map(label => (
                <li key={label}>
                  <button
                    type="button"
                    className="caption-alt"
                    onClick={() => sendFromFixed(`How do I agentify ${label.toLowerCase()}?`)}
                    style={{
                      background: 'transparent',
                      border: 0,
                      padding: 0,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10.98px',
                      textTransform: 'uppercase',
                      cursor: 'pointer',
                    }}>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      className="arrow-new">
                      <path
                        d="M7.97893 12.3126L8.68597 13.0197L13.7057 7.99994L8.68597 2.98022L7.97893 3.68726L11.7917 7.49999H1.99976V8.49989H11.7917L7.97893 12.3126Z"
                        fill="#FA4616"></path>
                    </svg>
                    {label}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
      {/* ******** fixed search end ******** */}

      {/* ******** ai popup start ******** */}
      {popupOpen && (
        <div className="ai__popup" style={{ display: 'block' }}>
          <div className="overlay--popup"></div>
          <div className="main__container">
            <div className="top__panel" style={{ position: 'relative' }}>
              <div className="top__panel--left">
                <div className="panel--icon">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none">
                    <path
                      d="M16.602 11.1769C12.6462 10.5066 9.52469 7.2136 8.88938 3.04052C8.8774 2.96175 8.78064 2.96175 8.76865 3.04052C8.13335 7.2136 5.0118 10.5066 1.05603 11.1769C0.981323 11.1896 0.981323 11.2917 1.05603 11.3043C5.0118 11.9745 8.13335 15.2677 8.76865 19.4407C8.78064 19.5195 8.87741 19.5195 8.88938 19.4407C9.52469 15.2677 12.6462 11.9745 16.602 11.3043C16.6767 11.2917 16.6767 11.1896 16.602 11.1769ZM12.7155 11.2725C10.7376 11.6076 9.17685 13.2541 8.8592 15.3407C8.85321 15.3801 8.80482 15.3801 8.79883 15.3407C8.48118 13.2541 6.9204 11.6076 4.94252 11.2725C4.90517 11.2662 4.90517 11.2151 4.94252 11.2088C6.9204 10.8736 8.48118 9.22711 8.79883 7.14057C8.80482 7.10119 8.85321 7.10119 8.8592 7.14057C9.17685 9.22711 10.7376 10.8736 12.7155 11.2088C12.7529 11.2151 12.7529 11.2662 12.7155 11.2725Z"
                      fill="#FA4616"
                    />
                    <path
                      d="M18.9719 4.66145C16.994 4.99652 15.4332 6.64311 15.1156 8.72965C15.1096 8.76903 15.0612 8.76903 15.0552 8.72965C14.7376 6.64311 13.1768 4.99652 11.1989 4.66145C11.1616 4.65513 11.1616 4.60406 11.1989 4.59774C13.1768 4.2626 14.7376 2.61608 15.0552 0.529537C15.0612 0.490154 15.1096 0.490154 15.1156 0.529537C15.4332 2.61608 16.994 4.2626 18.9719 4.59774C19.0092 4.60406 19.0092 4.65513 18.9719 4.66145Z"
                      fill="#FA4616"
                    />
                  </svg>
                </div>
                <h4 className="lead-S" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  AI search agent
                  <span className="tooltip-container">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none">
                      <circle cx="8" cy="8" r="7" stroke="#526069" strokeWidth="1.5" />
                      <path
                        d="M8 5.2a.9.9 0 1 0 0-1.8.9.9 0 0 0 0 1.8ZM7.2 6.7h1.6v5.1H7.2V6.7Z"
                        fill="#526069"
                      />
                    </svg>
                    <span className="custom-tooltip micro">
                      AI-generated content. May be inaccurate—verify with sources.&nbsp;
                      <a
                        href="https://www.uipath.com/legal/trust-and-security"
                        target="_blank"
                        rel="noopener noreferrer">
                        Learn more
                      </a>
                    </span>
                  </span>
                  <button
                    type="button"
                    className="ai-disclaimer-trigger"
                    aria-label="About this tool"
                    onMouseEnter={() => setShowDisclaimer(true)}
                    onMouseLeave={() => setShowDisclaimer(false)}
                    style={{ marginLeft: 8 }}></button>
                </h4>
              </div>
              <div className="top__panel--right">
                <button
                  type="button"
                  className="ga-clearChat chat-clear-button"
                  title="Start new chat"
                  aria-label="Start new chat"
                  onClick={clearChat}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    marginRight: 10,
                    padding: 4,
                  }}>
                  {/* New Chat (sparkles/refresh) icon */}
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none">
                    <path
                      d="M2.50381 7.9985C2.50381 4.96123 4.96599 2.49905 8.00326 2.49905C9.85653 2.49905 11.4684 3.41637 12.4572 4.82859L11.0044 4.82434L11.0015 5.82423L14.1693 5.83351L14.1693 2.6657H13.1694L13.1694 4.10713C11.9958 2.52646 10.1367 1.49915 8.00326 1.49915C4.41376 1.49915 1.50391 4.409 1.50391 7.9985H2.50381Z"
                      fill="#526069"
                    />
                    <path
                      d="M13.5034 7.99841C13.5034 11.0357 11.0412 13.4979 8.00394 13.4979C6.15067 13.4979 4.53881 12.5805 3.54998 11.1683L5.00278 11.1726L5.00571 10.1727L1.83789 10.1634L1.83789 13.3312H2.83779L2.83779 11.8898C4.01139 13.4704 5.87054 14.4978 8.00394 14.4978C11.5934 14.4978 14.5033 11.5879 14.5033 7.99841H13.5034Z"
                      fill="#526069"
                    />
                  </svg>
                </button>
                <div
                  className="ga-closeChat close--icon chat-close"
                  title="Close Chat"
                  onClick={closePopup}
                  style={{ cursor: 'pointer' }}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none">
                    <path
                      d="M5.33317 15.8332L4.1665 14.6665L8.83317 9.99984L4.1665 5.33317L5.33317 4.1665L9.99984 8.83317L14.6665 4.1665L15.8332 5.33317L11.1665 9.99984L15.8332 14.6665L14.6665 15.8332L9.99984 11.1665L5.33317 15.8332Z"
                      fill="#526069"
                    />
                  </svg>
                </div>
              </div>
            </div>

            <div className="chat-content">
              <div
                id="chatMessages"
                className={`chat-messages${showSources ? ' sourceAdd' : ''}`}
                ref={chatRef}>
                {messages.map((m, i) => (
                  <div className={`mainDiv  ${i % 2 === 0 ? 'even' : 'odd'}`} key={i}>
                    {m.role === 'user' ? (
                      <React.Fragment>
                        <div
                          className="chat-message user"
                          ref={el => {
                            if (el) msgRefs.current.set(i, el)
                            else msgRefs.current.delete(i)
                          }}>
                          {m.text}
                        </div>
                        <h6>Just now</h6>
                      </React.Fragment>
                    ) : m.kind === 'thinking' ? (
                      <div className="chat-message bot loading">
                        <div className="md-thinking">
                          <ThinkingDots />
                        </div>
                        <div className="md-stream" />
                        <div className="md-extras" />
                      </div>
                    ) : (
                      <div className="chat-message bot">
                        <div
                          className="md-stream"
                          dangerouslySetInnerHTML={{ __html: processLinks(m.html || '') }}
                        />
                        {/* Featured links always render (also in history) */}
                        <LinksBlock links={m.extras?.featuredLinks || []} />

                        {/* Show feedback + sources ONLY when meta (featured links, nextQuestions, or sources) has arrived for the latest turn */}
                        {i === lastModelIndex && hasAnyMeta(m) && !hideNextQuestions && (
                          <FeedbackAndCTA
                            modelIdx={i}
                            sourcesAvailable={currentSources.length > 0}
                            showSources={showSources}
                            onToggleSources={() => setShowSources(s => !s)}
                          />
                        )}

                        {/* Next questions remain tied to the latest turn, but also wait for meta */}
                        {i === lastModelIndex && !hideNextQuestions && hasAnyMeta(m) && (
                          <NextQuestionsBlock
                            items={m.extras?.nextQuestions || []}
                            disabled={busy}
                            onAsk={(q: string) => {
                              setHideNextQuestions(true)
                              handleSend(q)
                            }}
                          />
                        )}
                      </div>
                    )}
                  </div>
                ))}
                {/* extra space so the latest thinking bubble can align to the very top */}
                {needsBottomSpacer && <div aria-hidden className="chat-bottom-spacer" />}
              </div>

              <div className="source__panel" style={{ display: showSources ? 'block' : 'none' }}>
                <div className="source__panel--heading">
                  <h6 className="micro-alt heading__tag">SOURCES &amp; REFERENCES</h6>
                  <div className="close__icon--bar">
                    <a
                      href="#"
                      className="caption"
                      onClick={e => {
                        e.preventDefault()
                        setShowSources(false)
                      }}>
                      Hide
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none">
                        <path
                          d="M8.94369 8.00027L12.4719 4.47201L11.5292 3.5293L8.00098 7.05755L4.47281 3.52938L3.53009 4.47209L7.05826 8.00026L3.53003 11.5285L4.47274 12.4712L8.00098 8.94298L11.5293 12.4713L12.472 11.5286L8.94369 8.00027Z"
                          fill="black"
                        />
                      </svg>
                    </a>
                  </div>
                </div>
                <div className="source__panel--api">
                  <ul className="source-list">
                    {currentSources.map((s: any, idx: number) => (
                      <li key={idx}>
                        <a
                          href={s.url || '#'}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="caption">
                          {s.title || (s.url || '').replace(/^https?:\/\//, '')}
                        </a>
                        {s.description ? <p className="caption">{s.description}</p> : null}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <button
                id="scrollButton"
                className={showSources ? 'sourceAdd' : ''}
                ref={scrollBtnRef}
                onClick={() => {
                  if (!chatRef.current) return
                  chatRef.current.scrollTo({
                    top: chatRef.current.scrollHeight,
                    behavior: 'smooth',
                  })
                  // hide button after the smooth scroll completes
                  setTimeout(() => updateScrollButton(), 250)
                }}>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  xmlns="http://www.w3.org/2000/svg"
                  className="icon text-token-text-primary">
                  <path d="M9.33468 3.33333C9.33468 2.96617 9.6326 2.66847 9.99972 2.66829C10.367 2.66829 10.6648 2.96606 10.6648 3.33333V15.0609L15.363 10.3626L15.4675 10.2777C15.7255 10.1074 16.0762 10.1357 16.3034 10.3626C16.5631 10.6223 16.5631 11.0443 16.3034 11.304L10.4704 17.137C10.2108 17.3967 9.7897 17.3966 9.52999 17.137L3.69601 11.304L3.61105 11.1995C3.44054 10.9414 3.46874 10.5899 3.69601 10.3626C3.92328 10.1354 4.27479 10.1072 4.53292 10.2777L4.63741 10.3626L9.33468 15.0599V3.33333Z"></path>
                </svg>
              </button>

              <div className={`hero__search slideInBottom${showSources ? ' sourceAdd' : ''}`}>
                <div className="input__design">
                  <input
                    id="chat__input"
                    type="text"
                    placeholder={placeholderText}
                    value={input}
                    className="body-L"
                    onChange={e => setInput(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        handleSend()
                      }
                    }}
                  />
                  <div className="submit__design">
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 14 14"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      style={{ visibility: 'hidden' }}>
                      <path
                        d="M1.00146 5.66667L1.00146 8.33333M4.00146 3L4.00147 11M7.00147 1V13M10.0015 3V11M13.0015 5.66667V8.33333"
                        stroke="#B4C5CC"
                        strokeWidth="1.33333"
                        strokeLinecap="round"
                        strokeLinejoin="round"></path>
                    </svg>
                    <button
                      type="button"
                      id="fetch__ai__response"
                      onClick={() => handleSend()}
                      disabled={busy || !input.trim()}>
                      <span>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="none">
                          <path
                            d="M2.6665 8.00033L3.6065 8.94033L7.33317 5.22033V13.3337H8.6665V5.22033L12.3865 8.94699L13.3332 8.00033L7.99984 2.66699L2.6665 8.00033Z"
                            fill="#F8F9FA"
                          />
                        </svg>
                      </span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* ******** ai popup end ******** */}

      {/* open popup from either fixed input CTA button */}
      {fixedOpen && (
        <button
          style={{ position: 'fixed', right: 24, bottom: 24, zIndex: 2000, display: 'none' }}
          aria-hidden
          onClick={openPopup}
        />
      )}
    </>
  )
}

export default AskAi
